import React, { useCallback } from 'react';
import classNames from 'classnames';
import { useIntl } from 'umi';
import { Button, Modal } from 'tea-component';

import YamlEditor from '@/pages/datalab/components/YamlEditor';
import { COMMON_COMPLETION } from '@/pages/datalab/components/YamlEditor/constant';

import { WizardStep } from '../../constant';
import type { IStepYamlReviewProps } from '../../types';

import './index.scss';

/**
 * 向导第三步：展示 / 编辑 YAML，并提供重新生成与摘要区。
 * - yamlText：编辑器绑定内容；yamlReady：是否已有接口成功返回的 YAML，用于状态条「就绪」判断。
 * - yamlLoading / yamlError：与父组件 generate_yaml_from_config 请求同步。
 * - cardSummary：表 / 维度 / 指标摘要由父组件从 Step1、Step2 统一计算后传入。
 * - 「下一步」：由父级传入 `handleNext` 处理。
 */
const StepYamlReview: React.FC<IStepYamlReviewProps> = (props) => {
  const {
    setCurrent,
    yamlDraft,
    onYamlDraftChange,
    onRegenerateYaml,
    cardSummary,
    saveDraftLoading,
    handleNext,
  } = props;
  const { yamlReady, yamlText, yamlLoading, yamlError } = yamlDraft;
  const { tablesSummary, dimCount, measCount } = cardSummary;
  const { formatMessage } = useIntl();

  /** 返回 Step2：字段配置 */
  const handlePrev = (): void => {
    setCurrent(WizardStep.Two);
  };

  /** 编辑器变更仅写 yamlText；yamlReady 由父级在 generate / retrieve 成功时更新 */
  const handleYamlChange = useCallback(
    (value: string): void => {
      onYamlDraftChange({ yamlText: value });
    },
    [onYamlDraftChange],
  );

  /** 重新生成前二次确认，避免覆盖用户未保存的编辑 */
  const handleRegenerateClick = (): void => {
    Modal.confirm({
      className: 'dbl-model-agent-create-modal',
      message: formatMessage({
        id: 'dbl_model_agent_yaml_regenerate_confirm',
      }),
      okText: formatMessage({ id: 'dbl_common_confirm' }),
      cancelText: formatMessage({ id: 'dbl_common_cancel' }),
    }).then((confirmed: boolean) => {
      if (confirmed) {
        onRegenerateYaml();
      }
    });
  };

  /** 生成中、保存中或当前无 YAML 内容时不允许进入下一步 */
  const nextDisabled =
    yamlLoading || saveDraftLoading || !yamlText.trim();

  /** 状态条样式：请求中 > 失败 > 已有成功基准 > 空闲 */
  const getStatusClass = (): string => {
    if (yamlLoading) {
      return 'is-busy';
    }
    if (yamlError) {
      return 'is-error';
    }
    if (yamlReady) {
      return 'is-ready';
    }
    return 'is-idle';
  };

  /** 与 getStatusClass 对应的 i18n 文案 key */
  const getStatusKey = (): string => {
    if (yamlLoading) {
      return 'dbl_model_agent_yaml_status_generating';
    }
    if (yamlError) {
      return 'dbl_model_agent_yaml_status_error';
    }
    if (yamlReady) {
      return 'dbl_model_agent_yaml_status_ready';
    }
    return 'dbl_model_agent_yaml_status_idle';
  };

  return (
    <>
      <div className="dbl-model-agent-create-step dbl-model-agent-create-step-yaml-review">
        {/* 标题 + 状态 + 重新生成 */}
        <div className="dbl-model-agent-create-step-yaml-review-header">
          <strong>
            {formatMessage({ id: 'dbl_model_agent_yaml_file_label' })}
          </strong>
          <span
            className={classNames(
              'dbl-model-agent-create-step-yaml-review-status',
              getStatusClass(),
            )}
          >
            {formatMessage({ id: getStatusKey() })}
          </span>
          <Button
            type="weak"
            className="dbl-model-agent-create-step-yaml-review-regen"
            disabled={yamlLoading}
            onClick={handleRegenerateClick}
          >
            {formatMessage({ id: 'dbl_model_agent_yaml_regenerate' })}
          </Button>
        </div>
        {/* yamlError 时加 is-error 边框提示 */}
        <div
          className={classNames(
            'dbl-model-agent-create-step-yaml-review-editor',
            { 'is-error': yamlError },
          )}
        >
          <YamlEditor
            value={yamlText}
            completions={COMMON_COMPLETION}
            onChange={(value: string, _isValid: boolean) => {
              handleYamlChange(value);
            }}
          />
        </div>
        {/* 表 / 维度 / 指标摘要，与 model.html Step3 摘要区一致 */}
        <div className="dbl-model-agent-create-card-list">
          <div className="dbl-model-agent-create-card">
            <div className="dbl-model-agent-create-card-title">
              {formatMessage({ id: 'dbl_model_agent_summary_tables' })}
            </div>
            <div
              className="dbl-model-agent-create-card-content dbl-model-agent-create-step-yaml-review-summary-value"
              title={tablesSummary}
            >
              {tablesSummary}
            </div>
          </div>
          <div className="dbl-model-agent-create-card">
            <div className="dbl-model-agent-create-card-title">
              {formatMessage({ id: 'dbl_model_agent_summary_dimensions' })}
            </div>
            <div className="dbl-model-agent-create-card-content">
              {formatMessage(
                { id: 'dbl_model_agent_summary_count_unit' },
                { n: dimCount },
              )}
            </div>
          </div>
          <div className="dbl-model-agent-create-card">
            <div className="dbl-model-agent-create-card-title">
              {formatMessage({ id: 'dbl_model_agent_summary_measures' })}
            </div>
            <div className="dbl-model-agent-create-card-content">
              {formatMessage(
                { id: 'dbl_model_agent_summary_count_unit' },
                { n: measCount },
              )}
            </div>
          </div>
        </div>
      </div>
      {/* 与 StepFieldConfig 等子步骤共用页脚样式 */}
      <div className="dbl-model-agent-create-footer">
        <Button type="weak" onClick={handlePrev}>
          {formatMessage({ id: 'dbl_model_management_create_model_previous_step' })}
        </Button>
        <Button
          type="primary"
          disabled={nextDisabled}
          loading={saveDraftLoading}
          onClick={() => {
            handleNext();
          }}
        >
          {formatMessage({ id: 'dbl_model_management_create_model_next_step' })}
        </Button>
      </div>
    </>
  );
};

export default StepYamlReview;
